/**
 * The signed-in user's projects.
 *
 *  - syncMyProjects(): asks the Cloud Function to import/refresh the account's
 *    projects from the legacy backend, a few per call, until none is pending.
 *  - watchMyProjects(): live list of summaries (users/{uid}/projects) that the
 *    function keeps up to date; watchAccount(): folders etc. (users/{uid}).
 */
import { getApp, FUNCTIONS_REGION } from "../firebase";
import { currentLanguage } from "../player/i18n";
import { storageUrl } from "./media";
import { subscriptionOf, type Subscription } from "./subscription";

export interface ProjectSummary {
  id: string;
  name: string;
  type: string;
  language: string;
  template: string;
  slideCount: number;
  thumb: string | null;
  shareCode: string | null;
  updated: number;
  source: "php" | "web";
  /** in the bin since then (ms), see BIN_DAYS */
  deletedAt?: number | null;
}

/** Days a project stays in the bin before it is deleted for good (the app's "Recently Deleted"). */
export const BIN_DAYS = 30;

/** ProjectsManager.LoadDeletedProjects: 30 − whole days since it was deleted, never below 0. */
export function binDaysLeft(deletedAt: number, now = Date.now()): number {
  return Math.max(0, BIN_DAYS - Math.floor((now - deletedAt) / 86_400_000));
}

/** A Firestore timestamp (or millis) → millis. */
function millisOf(v: unknown): number | null {
  if (typeof v === "number") return v;
  const ts = v as { toMillis?: () => number } | null | undefined;
  return ts?.toMillis ? ts.toMillis() : null;
}

export interface Folder {
  id: number;
  name: string;
  projects: string[];
}

export interface Account {
  email: string;
  serverId: number;
  folders: Folder[];
  settings?: AccountSettings;
  /** Make It Premium (functions › subscription.ts) */
  subscription: Subscription | null;
}

export interface SyncProgress {
  total: number;
  done: number;
  failed: number;
}

export class SyncError extends Error {
  constructor(
    public readonly code: "no-legacy-account" | "unauthenticated" | "network" | "unknown",
    message?: string,
  ) {
    super(message ?? code);
    this.name = "SyncError";
  }
}

export async function syncMyProjects(onProgress?: (p: SyncProgress) => void): Promise<{ total: number; failed: string[] }> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fns = getFunctions(getApp(), FUNCTIONS_REGION);
  type Res = { total: number; imported: number; pending: number; queue?: string[]; failed: string[]; gone: string[]; folders: Folder[] };
  const call = httpsCallable<{ max: number; skip: string[]; queue?: string[]; lang: string }, Res>(fns, "syncMyProjects", { timeout: 320_000 });
  // a new account gets the demos in the web's language (giveDemos in the functions)
  const lang = currentLanguage();
  const failed: string[] = [];
  let total = 0;
  // The first call works out what to import and hands back the queue; the next ones
  // work through it, however long it is (no cap: an account may have hundreds).
  let queue: string[] | undefined;
  let stalls = 0;
  for (;;) {
    let res: Res | null = null;
    for (let attempt = 0; !res; attempt++) {
      try {
        res = (await call({ max: 6, skip: failed, queue, lang })).data;
      } catch (err) {
        const code = String((err as { code?: string })?.code ?? "");
        const msg = String((err as { message?: string })?.message ?? "");
        if (code === "functions/failed-precondition" && msg.includes("no-legacy-account")) throw new SyncError("no-legacy-account");
        if (code === "functions/unauthenticated") throw new SyncError("unauthenticated");
        const transient = code === "functions/unavailable" || code === "functions/internal" || code === "functions/deadline-exceeded";
        // a dropped connection or a slow round in a long import: wait and try that round again
        if (transient && attempt < 3) {
          await new Promise((r) => setTimeout(r, 3000 * (attempt + 1)));
          continue;
        }
        if (transient) throw new SyncError("network", msg);
        throw new SyncError("unknown", msg);
      }
    }
    total = res.total;
    failed.push(...res.failed);
    onProgress?.({ total, done: Math.max(0, total - res.pending), failed: failed.length });
    const next = res.queue ?? [];
    if (!next.length || res.pending <= 0) break;
    // the queue must get shorter; if it does not for a few rounds, leave the rest for next time
    stalls = queue && next.length >= queue.length ? stalls + 1 : 0;
    if (stalls >= 3) break;
    queue = next;
  }
  return { total, failed };
}

export async function watchMyProjects(uid: string, cb: (list: ProjectSummary[]) => void): Promise<() => void> {
  const { getFirestore, collection, onSnapshot, query, orderBy } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  const q = query(collection(db, "users", uid, "projects"), orderBy("name"));
  return onSnapshot(
    q,
    (snap) => {
      cb(
        snap.docs.map((d) => {
          const v = d.data() as Partial<ProjectSummary> & { deletedAt?: unknown };
          return {
            id: d.id,
            deletedAt: millisOf(v.deletedAt),
            name: v.name ?? "",
            type: v.type ?? "game",
            language: v.language ?? "unknown",
            template: v.template ?? "none",
            slideCount: v.slideCount ?? 0,
            thumb: v.thumb ?? null,
            shareCode: v.shareCode ?? null,
            updated: v.updated ?? 0,
            source: v.source === "web" ? "web" : "php",
          };
        }),
      );
    },
    () => cb([]),
  );
}

export async function watchAccount(uid: string, cb: (acc: Account | null) => void): Promise<() => void> {
  const { getFirestore, doc, onSnapshot } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  return onSnapshot(
    doc(db, "users", uid),
    (snap) => {
      if (!snap.exists()) return cb(null);
      const v = snap.data() as Partial<Account>;
      cb({ email: v.email ?? "", serverId: v.serverId ?? 0, folders: v.folders ?? [], settings: v.settings, subscription: subscriptionOf((snap.data() as Record<string, unknown>).subscription) });
    },
    () => cb(null),
  );
}

export const thumbUrl = (p: ProjectSummary) => (p.thumb ? `${storageUrl(p.thumb)}&t=${p.updated}` : null);

// ---------------------------------------------------------------------------
// Mutations (manageProject / manageFolder Cloud Functions): everything stays in Firebase.
// ---------------------------------------------------------------------------

async function callFn<I, O>(name: string, data: I): Promise<O> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fns = getFunctions(getApp(), FUNCTIONS_REGION);
  const call = httpsCallable<I, O>(fns, name);
  try {
    return (await call(data)).data;
  } catch (err) {
    const code = String((err as { code?: string })?.code ?? "");
    const msg = String((err as { message?: string })?.message ?? "");
    if (code === "functions/unauthenticated") throw new SyncError("unauthenticated");
    if (code === "functions/unavailable" || code === "functions/internal" || code === "functions/deadline-exceeded") throw new SyncError("network", msg);
    throw new SyncError("unknown", msg);
  }
}

export type ProjectKind = "game" | "quiz";

export const createProject = (name: string, type: ProjectKind, template: string, language: string) =>
  callFn<{ action: "create"; name: string; type: ProjectKind; template: string; language: string }, { projectId: string }>("manageProject", { action: "create", name, type, template, language });
export const renameProject = (projectId: string, name: string) => callFn<{ action: "rename"; projectId: string; name: string }, { ok: true }>("manageProject", { action: "rename", projectId, name });
/** To the bin ("Recently Deleted"): restoreProject brings it back, purgeProject (or BIN_DAYS) deletes it for good. */
export const deleteProject = (projectId: string) => callFn<{ action: "delete"; projectId: string }, { ok: true }>("manageProject", { action: "delete", projectId });
export const restoreProject = (projectId: string) => callFn<{ action: "restore"; projectId: string }, { ok: true }>("manageProject", { action: "restore", projectId });
export const purgeProject = (projectId: string) => callFn<{ action: "purge"; projectId: string }, { ok: true }>("manageProject", { action: "purge", projectId });
export const duplicateProject = (projectId: string) => callFn<{ action: "duplicate"; projectId: string }, { projectId: string }>("manageProject", { action: "duplicate", projectId });
export const shareProject = (projectId: string) => callFn<{ action: "share"; projectId: string }, { shareCode: string }>("manageProject", { action: "share", projectId });
export const setProjectFolder = (projectId: string, folderId: number | null) =>
  callFn<{ action: "setFolder"; projectId: string; folderId: number | null }, { ok: true }>("manageProject", { action: "setFolder", projectId, folderId });
/** AddToFoldersPanel: the project is in exactly these folders (it can be in several). */
export const setProjectFolders = (projectId: string, folderIds: number[]) =>
  callFn<{ action: "setFolders"; projectId: string; folderIds: number[] }, { folders: Folder[] }>("manageProject", { action: "setFolders", projectId, folderIds });

export const createFolder = (name: string) => callFn<{ action: "create"; name: string }, { folders: Folder[] }>("manageFolder", { action: "create", name });
export const renameFolder = (folderId: number, name: string) => callFn<{ action: "rename"; folderId: number; name: string }, { folders: Folder[] }>("manageFolder", { action: "rename", folderId, name });
/** "Add Projects" in a folder (ProjectsToFoldersPanel). */
export const addProjectsToFolder = (folderId: number, projects: string[]) => callFn<{ action: "addProjects"; folderId: number; projects: string[] }, { folders: Folder[] }>("manageFolder", { action: "addProjects", folderId, projects });
export const deleteFolder = (folderId: number) => callFn<{ action: "delete"; folderId: number }, { folders: Folder[] }>("manageFolder", { action: "delete", folderId });

// ---------------------------------------------------------------------------
// Editor: save the whole project, upload / copy media, online pictures
// ---------------------------------------------------------------------------

export const saveProjectDoc = (project: import("@makeit/schema").Project) =>
  callFn<{ action: "save"; projectId: string; project: unknown }, { ok: true; lastUpdateTime: number }>("manageProject", { action: "save", projectId: project.id, project });

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return btoa(bin);
}

/** Uploads a picture or a sound to projects/<id>/media/ and returns its file name. */
export async function uploadMedia(projectId: string, kind: "image" | "sound", blob: Blob): Promise<string> {
  const base64 = await blobToBase64(blob);
  const r = await callFn<{ action: "uploadMedia"; projectId: string; kind: string; contentType: string; base64: string }, { fileName: string }>("manageProject", {
    action: "uploadMedia",
    projectId,
    kind,
    contentType: blob.type || (kind === "image" ? "image/png" : "audio/wav"),
    base64,
  });
  return r.fileName;
}

export const copyMedia = (projectId: string, fromProjectId: string, files: string[]) =>
  callFn<{ action: "copyMedia"; projectId: string; fromProjectId: string; files: string[] }, { ok: true }>("manageProject", { action: "copyMedia", projectId, fromProjectId, files });

export interface OnlineImage {
  id: string;
  thumbnailUrl: string;
  type: string;
}
export const searchOnlineImages = (term: string, types: "vector" | "photo") =>
  callFn<{ action: "search"; term: string; types: string }, { items: OnlineImage[] }>("imageSearch", { action: "search", term, types }).then((r) => r.items);

export async function fetchOnlineImage(resourceId: string): Promise<Blob> {
  const r = await callFn<{ action: "fetch"; resourceId: string }, { contentType: string; base64: string }>("imageSearch", { action: "fetch", resourceId });
  const bin = atob(r.base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: r.contentType || "image/png" });
}

/** Full project document (for the project page: slides, settings). */
export async function watchProject(projectId: string, cb: (p: import("@makeit/schema").Project | null, meta: ProjectMeta | null) => void): Promise<() => void> {
  const { getFirestore, doc, onSnapshot } = await import("firebase/firestore");
  const { parseProject } = await import("@makeit/schema");
  const db = getFirestore(getApp());
  return onSnapshot(
    doc(db, "projects", projectId),
    (snap) => {
      if (!snap.exists()) return cb(null, null);
      const { meta, ...data } = snap.data() as Record<string, unknown> & { meta?: ProjectMeta };
      try {
        cb(parseProject(data), (meta as ProjectMeta) ?? null);
      } catch {
        cb(null, null);
      }
    },
    () => cb(null, null),
  );
}

export interface ProjectMeta {
  ownerUid?: string;
  editedAt?: unknown;
  source?: string;
  thumb?: string | null;
  screenshots?: number[];
  shareCode?: string | null;
  slideCount?: number;
}

export const screenshotUrl = (projectId: string, slideId: number, bust: number) => `${storageUrl(`projects/${projectId}/screenshots/${slideId}.png`)}&t=${bust}`;

// ---------------------------------------------------------------------------
// Shared with me (users/{uid}/shared), account settings, classrooms
// ---------------------------------------------------------------------------

export const redeemCode = (code: string) => callFn<{ action: "redeem"; code: string }, { projectId: string }>("manageProject", { action: "redeem", code });
export const forgetShared = (projectId: string) => callFn<{ action: "forget"; projectId: string }, { ok: true }>("manageProject", { action: "forget", projectId });

export async function watchShared(uid: string, cb: (list: ProjectSummary[]) => void): Promise<() => void> {
  const { getFirestore, collection, onSnapshot, query, orderBy } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  const q = query(collection(db, "users", uid, "shared"), orderBy("addedAt", "desc"));
  return onSnapshot(
    q,
    (snap) =>
      cb(
        snap.docs.map((d) => {
          const v = d.data() as Partial<ProjectSummary>;
          return { id: d.id, name: v.name ?? "", type: v.type ?? "game", language: v.language ?? "unknown", template: v.template ?? "none", slideCount: v.slideCount ?? 0, thumb: v.thumb ?? null, shareCode: v.shareCode ?? null, updated: v.updated ?? 0, source: "php" };
        }),
      ),
    () => cb([]),
  );
}

/** How the Home lists projects, as the app's two buttons (AppSettingsManager.ProjectsOrder). */
export type ProjectsOrder = "nameAsc" | "nameDesc" | "dateDesc" | "dateAsc";

export interface AccountSettings {
  language?: string;
  defaultSeconds: number;
  defaultFont: string | null;
  allowCloning: boolean;
  /** kept with the account, the same on every device; newest first until chosen */
  projectsOrder?: ProjectsOrder;
}
export const DEFAULT_SETTINGS: AccountSettings = { defaultSeconds: 20, defaultFont: null, allowCloning: true };

/** «Add the demos» of an empty "My projects": copies of the demos in the web's language (manageAccount › addDemos). */
export const addDemos = () => callFn<{ action: "addDemos"; lang: string }, { added: number }>("manageAccount", { action: "addDemos", lang: currentLanguage() });

export const saveProjectsOrder = (order: ProjectsOrder) => callFn<{ action: "projectsOrder"; order: ProjectsOrder }, { projectsOrder: ProjectsOrder }>("manageAccount", { action: "projectsOrder", order });

export const saveSettings = (settings: AccountSettings) => callFn<{ action: "settings"; settings: AccountSettings }, { settings: AccountSettings }>("manageAccount", { action: "settings", settings });

export interface Classroom {
  id: string;
  name: string;
  code: string;
  /** shared automatically with every student, new ones included */
  projects: string[];
  /** shared with some students only: studentId → project ids */
  studentProjects: Record<string, string[]>;
  studentCount: number;
}

/** Every project shared in the classroom, with all students or with some. */
export function classroomProjectIds(room: Classroom): string[] {
  return [...new Set([...room.projects, ...Object.values(room.studentProjects).flat()])];
}

/** The projects a student of the classroom sees. */
export function projectsForStudent(room: Classroom, studentId: string): string[] {
  return [...new Set([...room.projects, ...(room.studentProjects[studentId] ?? [])])];
}

/** How a project is shared in a classroom: with everybody, or the students that have it. */
export function sharingOf(room: Classroom, projectId: string): { all: boolean; students: string[] } {
  if (room.projects.includes(projectId)) return { all: true, students: [] };
  return { all: false, students: Object.entries(room.studentProjects).filter(([, l]) => l.includes(projectId)).map(([sid]) => sid) };
}
export interface Student {
  id: string;
  name: string;
  joinedAt: number;
}

export const createClassroom = (name: string) => callFn<{ action: "create"; name: string }, { classroomId: string; code: string }>("manageClassroom", { action: "create", name });
export const renameClassroom = (classroomId: string, name: string) => callFn<{ action: "rename"; classroomId: string; name: string }, { ok: true }>("manageClassroom", { action: "rename", classroomId, name });
export const deleteClassroom = (classroomId: string) => callFn<{ action: "delete"; classroomId: string }, { ok: true }>("manageClassroom", { action: "delete", classroomId });
export const setClassroomProjects = (classroomId: string, projects: string[]) => callFn<{ action: "setProjects"; classroomId: string; projects: string[] }, { projects: string[] }>("manageClassroom", { action: "setProjects", classroomId, projects });
/** ProjectClassroomsShare: share one project with every student (automatic) or only with some. */
export const setClassroomSharing = (classroomId: string, projectId: string, all: boolean, students: string[]) =>
  callFn<{ action: "setSharing"; classroomId: string; projectId: string; all: boolean; students: string[] }, { projects: string[]; studentProjects: Record<string, string[]> }>("manageClassroom", { action: "setSharing", classroomId, projectId, all, students });
export const removeStudent = (classroomId: string, studentId: string) => callFn<{ action: "removeStudent"; classroomId: string; studentId: string }, { ok: true }>("manageClassroom", { action: "removeStudent", classroomId, studentId });
export const renameStudent = (classroomId: string, studentId: string, name: string) =>
  callFn<{ action: "renameStudent"; classroomId: string; studentId: string; name: string }, { ok: true }>("manageClassroom", { action: "renameStudent", classroomId, studentId, name });
/** ClassroomsController.MoveStudent: to another of the teacher's classrooms. */
export const moveStudent = (classroomId: string, studentId: string, toClassroomId: string) =>
  callFn<{ action: "moveStudent"; classroomId: string; studentId: string; toClassroomId: string }, { ok: true }>("manageClassroom", { action: "moveStudent", classroomId, studentId, toClassroomId });

export interface JoinedClassroom {
  classroomId: string;
  studentId: string;
  name: string;
  classroomName: string;
  projects: string[];
}
export const joinClassroom = (code: string, name: string, studentId?: string | null) =>
  callFn<{ code: string; name: string; studentId?: string }, JoinedClassroom>("joinClassroom", { code, name, ...(studentId ? { studentId } : {}) });
/**
 * Where a student's device stands now: the classroom it is in (a teacher may have moved
 * it), its current name and projects; "gone" once the teacher removed it; null when the
 * server can't be reached (keep what is stored).
 */
export async function refreshStudent(j: JoinedClassroom): Promise<JoinedClassroom | "gone" | null> {
  try {
    return await callFn<{ refresh: true; classroomId: string; studentId: string }, JoinedClassroom>("joinClassroom", { refresh: true, classroomId: j.classroomId, studentId: j.studentId });
  } catch (err) {
    return err instanceof SyncError && /no-student/.test(err.message) ? "gone" : null;
  }
}

export async function watchClassrooms(uid: string, cb: (list: Classroom[]) => void): Promise<() => void> {
  const { getFirestore, collection, onSnapshot, query, where, orderBy } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  const q = query(collection(db, "classrooms"), where("ownerUid", "==", uid));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => classroomOf(d.id, d.data())).sort((a, b) => a.name.localeCompare(b.name))),
    () => cb([]),
  );
}

function classroomOf(id: string, v: Record<string, unknown>): Classroom {
  const studentProjects: Record<string, string[]> = {};
  for (const [sid, list] of Object.entries((v.studentProjects ?? {}) as Record<string, unknown>)) if (Array.isArray(list)) studentProjects[sid] = list.map(String);
  return { id, name: String(v.name ?? ""), code: String(v.code ?? ""), projects: Array.isArray(v.projects) ? v.projects.map(String) : [], studentProjects, studentCount: Number(v.studentCount ?? 0) || 0 };
}

export async function watchClassroom(classroomId: string, cb: (c: Classroom | null) => void): Promise<() => void> {
  const { getFirestore, doc, onSnapshot } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  return onSnapshot(
    doc(db, "classrooms", classroomId),
    (snap) => cb(snap.exists() ? classroomOf(snap.id, snap.data()) : null),
    () => cb(null),
  );
}

export async function watchStudents(classroomId: string, cb: (list: Student[]) => void): Promise<() => void> {
  const { getFirestore, collection, onSnapshot, query, orderBy } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  return onSnapshot(
    query(collection(db, "classrooms", classroomId, "students"), orderBy("name")),
    (snap) =>
      cb(
        snap.docs.map((d) => {
          const v = d.data() as { name?: string; joinedAt?: { toMillis?: () => number } };
          return { id: d.id, name: v.name ?? "", joinedAt: v.joinedAt?.toMillis?.() ?? 0 };
        }),
      ),
    () => cb([]),
  );
}

/** Light summary of any project (for shared / classroom lists): name, type, template, slide count. */
export async function fetchProjectSummary(projectId: string): Promise<ProjectSummary | null> {
  const { getFirestore, doc, getDoc } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  const snap = await getDoc(doc(db, "projects", projectId));
  if (!snap.exists()) return null;
  const v = snap.data() as Record<string, unknown>;
  const meta = (v.meta ?? {}) as Record<string, unknown>;
  const sharing = (v.sharing ?? {}) as { shareCode?: number };
  return {
    id: snap.id,
    name: String(v.name ?? ""),
    type: String(v.type ?? "game"),
    language: String(v.language ?? "unknown"),
    template: String(v.template ?? "none"),
    slideCount: Array.isArray(v.slides) ? v.slides.length : Number(meta.slideCount ?? 0) || 0,
    thumb: (meta.thumb as string | null) ?? null,
    shareCode: Number(sharing.shareCode ?? 0) > 0 ? String(sharing.shareCode) : null,
    updated: Number(v.lastUpdateTime ?? 0) || 0,
    source: meta.source === "web" ? "web" : "php",
    deletedAt: millisOf(meta.deletedAt),
  };
}

// ---------------------------------------------------------------------------
// Answers of a project (owner reports: HomeReportsPanel.cs)
// ---------------------------------------------------------------------------

export interface AnswerRecord {
  id: string;
  userName: string;
  answers: { slideId: number; answer: string; correct: boolean; points: number }[];
  totalPoints: number;
  finished: boolean;
  classroomId: string | null;
  studentId: string | null;
  createdAt: number;
}

export async function fetchAnswers(projectId: string, classroomId?: string): Promise<AnswerRecord[]> {
  const { getFirestore, collection, getDocs, query, where, orderBy, limit } = await import("firebase/firestore");
  const db = getFirestore(getApp());
  const base = collection(db, "projects", projectId, "answers");
  const q = classroomId ? query(base, where("classroomId", "==", classroomId), limit(500)) : query(base, orderBy("totalPoints", "desc"), limit(500));
  const snap = await getDocs(q);
  const list = snap.docs.map((d) => {
    const v = d.data() as Record<string, unknown>;
    return {
      id: d.id,
      userName: String(v.userName ?? "?"),
      answers: Array.isArray(v.answers) ? (v.answers as AnswerRecord["answers"]) : [],
      totalPoints: Number(v.totalPoints ?? 0) || 0,
      finished: v.finished === true,
      classroomId: typeof v.classroomId === "string" ? v.classroomId : null,
      studentId: typeof v.studentId === "string" ? v.studentId : null,
      createdAt: (v.createdAt as { toMillis?: () => number } | undefined)?.toMillis?.() ?? 0,
    };
  });
  list.sort((a, b) => b.totalPoints - a.totalPoints);
  return list;
}

function base64Blob(base64: string, contentType: string): Blob {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

/** A project media file's bytes, through the function (Storage needs no CORS setup for it). */
export async function readMedia(projectId: string, fileName: string): Promise<Blob> {
  const r = await callFn<{ action: "readMedia"; projectId: string; fileName: string }, { contentType: string; base64: string }>("manageProject", { action: "readMedia", projectId, fileName });
  return base64Blob(r.base64, r.contentType || "image/png");
}

export interface VideoResult {
  id: string;
  title: string;
  thumbnail: string;
}
/** "Search in YouTube" (SearchVideo.cs). */
export const searchVideos = (term: string) =>
  callFn<{ action: "search"; term: string }, { items: VideoResult[] }>("videoSearch", { action: "search", term }).then((r) => r.items);

/** The picture a video element shows (img.youtube.com/vi/<id>/0.jpg). */
export async function fetchVideoThumb(id: string): Promise<Blob> {
  const r = await callFn<{ action: "thumb"; id: string }, { contentType: string; base64: string }>("videoSearch", { action: "thumb", id });
  return base64Blob(r.base64, r.contentType || "image/jpeg");
}
